import { useEffect, useState } from 'react';
import { Keyboard, X } from '@phosphor-icons/react';

/**
 * KeyboardShortcutsOverlay
 * Press "?" anywhere to open the cheat sheet, "Esc" to close.
 *
 * Props:
 *   shortcuts: [{ keys: ['Ctrl', 'D'], desc: 'Copy from row above' }, ...]
 *   contextLabel: optional small badge (e.g. "Booking Workspace")
 *   tourKey: optional unique key (e.g. "booking-workspace") that enables a one-time
 *            first-login interactive tour pointing the user to the floating "?" FAB.
 *            Tracked in localStorage as `kbTour:<tourKey>`.
 */
export default function KeyboardShortcutsOverlay({ shortcuts = [], contextLabel, tourKey }) {
  const [open, setOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // ---- One-time interactive tour ----
  useEffect(() => {
    if (!tourKey) return;
    try {
      const seen = localStorage.getItem(`kbTour:${tourKey}`);
      if (!seen) {
        // Slight delay so the page has time to settle in
        const t = setTimeout(() => setShowTour(true), 900);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // localStorage may be unavailable (e.g. Safari private) — skip silently
    }
  }, [tourKey]);

  const dismissTour = () => {
    setShowTour(false);
    if (tourKey) {
      try { localStorage.setItem(`kbTour:${tourKey}`, '1'); } catch (e) { /* ignore */ }
    }
  };

  // Auto-dismiss tour after 14 seconds so it never lingers
  useEffect(() => {
    if (!showTour) return;
    const t = setTimeout(() => dismissTour(), 14000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTour]);

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isFormField = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      // Open on "?" — but not while typing into a form field
      if (e.key === '?' && !isFormField) {
        e.preventDefault();
        setOpen(o => !o);
        if (showTour) dismissTour();
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showTour]);

  const handleFabClick = () => {
    setOpen(true);
    if (showTour) dismissTour();
  };

  return (
    <>
      {/* Floating bottom-right hint button */}
      <div className="fixed bottom-4 right-4 z-40">
        {/* Pulsing rings — shown only during first-login tour */}
        {showTour && (
          <>
            <span className="pointer-events-none absolute inset-0 rounded-full bg-violet-400 opacity-60 animate-ping" />
            <span className="pointer-events-none absolute -inset-1 rounded-full bg-violet-300 opacity-30 animate-pulse" />
          </>
        )}
        <button
          type="button"
          onClick={handleFabClick}
          className={`relative flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs px-3 py-2 rounded-full shadow-lg transition-all hover:scale-105 ${
            showTour ? 'ring-4 ring-violet-300 ring-offset-2 scale-110' : ''
          }`}
          title='Keyboard shortcuts (press "?")'
          data-testid="kb-shortcuts-fab"
        >
          <Keyboard size={14} weight="fill" />
          <span className="hidden sm:inline">?</span>
        </button>

        {/* First-login tour callout */}
        {showTour && (
          <div
            className="absolute bottom-full right-0 mb-3 w-72 bg-white rounded-xl shadow-2xl border-2 border-violet-400 p-4 animate-in fade-in slide-in-from-bottom-2"
            data-testid="kb-shortcuts-tour"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center">
                  <Keyboard size={16} weight="fill" className="text-violet-600" />
                </div>
                <span className="text-sm font-bold text-gray-800">Tip: Speed up by 3×</span>
              </div>
              <button
                onClick={dismissTour}
                className="text-gray-400 hover:text-gray-700 -mt-1"
                aria-label="Dismiss tour"
                data-testid="kb-tour-dismiss"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              Press <kbd className="px-1.5 py-0.5 font-mono font-bold bg-gray-100 border border-gray-300 rounded text-[10px]">?</kbd> anytime
              (or click this button) to see all keyboard shortcuts —
              navigate rows like Excel, copy-paste, and save without touching the mouse.
            </p>
            <div className="flex items-center justify-between">
              <button
                onClick={handleFabClick}
                className="text-xs font-semibold text-violet-700 hover:text-violet-900 underline decoration-dotted"
                data-testid="kb-tour-show-now"
              >
                Show me now
              </button>
              <button
                onClick={dismissTour}
                className="text-xs text-gray-500 hover:text-gray-700"
                data-testid="kb-tour-got-it"
              >
                Got it
              </button>
            </div>
            {/* Pointer arrow to the FAB */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r-2 border-b-2 border-violet-400 transform rotate-45" />
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setOpen(false)}
          data-testid="kb-shortcuts-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-500 px-5 py-3 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Keyboard size={22} weight="fill" />
                <h2 className="text-base font-bold tracking-tight">Keyboard Shortcuts</h2>
                {contextLabel && (
                  <span className="text-[10px] uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">
                    {contextLabel}
                  </span>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-1 hover:bg-white/20"
                aria-label="Close"
                data-testid="kb-overlay-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-5 space-y-1">
              {shortcuts.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No shortcuts configured for this page.</p>
              ) : (
                shortcuts.map((s, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                      i % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                    } hover:bg-violet-50 transition-colors`}
                  >
                    <span className="text-sm text-gray-700">{s.desc}</span>
                    <span className="flex items-center gap-1 shrink-0 ml-3">
                      {s.keys.map((k, ki) => (
                        <span key={ki} className="flex items-center gap-1">
                          <kbd className="px-2 py-1 text-[11px] font-mono font-bold bg-gray-100 border border-gray-300 rounded shadow-sm text-gray-700 min-w-[28px] text-center">
                            {k}
                          </kbd>
                          {ki < s.keys.length - 1 && <span className="text-gray-400 text-xs">+</span>}
                        </span>
                      ))}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-5 py-2.5 text-[11px] text-gray-500 flex items-center justify-between border-t">
              <span>
                Press <kbd className="px-1.5 py-0.5 font-mono font-bold bg-white border border-gray-300 rounded text-[10px]">?</kbd> anywhere to toggle this help
              </span>
              <span>
                Press <kbd className="px-1.5 py-0.5 font-mono font-bold bg-white border border-gray-300 rounded text-[10px]">Esc</kbd> to close
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
